// frontend/src/components/Portfolio/ScenarioComparisonTool.tsx
// Advanced Scenario Comparison Tool with:
// - Side-by-side scenario comparison
// - "What if we divest from high-risk locations?"
// - Portfolio value under different climate futures
// - Interactive divestment simulation

import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Slider,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Tabs,
  Tab,
  Divider,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  CompareArrows as CompareIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Download as DownloadIcon,
  PlayArrow as SimulateIcon,
  Refresh as ResetIcon,
  Delete as DivestIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  Speed as SpeedIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  ReferenceLine,
} from 'recharts';

// ============================================================================
// INTERFACES
// ============================================================================
interface PortfolioItem {
  postal_code?: string;
  city?: string;
  state?: string;
  asset_value?: number | string;
  risk_score?: number;
  total_damage_10?: number;
  total_event_count_10?: number;
  flood?: number;
  wildfire?: number;
  hurricane?: number;
  earthquake?: number;
  heat?: number;
  drought?: number;
  [key: string]: any;
}

interface ScenarioComparisonToolProps {
  portfolio: {
    items: PortfolioItem[];
    name?: string;
    total_value?: number;
    [key: string]: any;
  };
}

interface ClimateScenario {
  id: string;
  name: string;
  shortName: string;
  description: string;
  ssp: string;
  warming: string;
  color: string;
  factors: { [year: number]: number };
}

interface DivestmentStrategy {
  id: string;
  name: string;
  description: string;
  threshold: number;
  metric: 'risk_score' | 'flood' | 'wildfire' | 'hurricane' | 'all_hazards';
}

// ============================================================================
// CLIMATE SCENARIOS (IPCC AR6)
// ============================================================================
const CLIMATE_SCENARIOS: ClimateScenario[] = [
  {
    id: 'ssp126',
    name: 'Low Emissions (SSP1-2.6)',
    shortName: 'SSP1-2.6',
    description: 'Strong mitigation, rapid emissions reductions, global cooperation',
    ssp: 'SSP1-2.6',
    warming: '1.5-2.0°C',
    color: '#10B981',
    factors: { 0: 1.0, 2: 1.03, 4: 1.06, 6: 1.10, 8: 1.15, 10: 1.20 },
  },
  {
    id: 'ssp245',
    name: 'Middle-of-the-Road (SSP2-4.5)',
    shortName: 'SSP2-4.5',
    description: 'Current policy trajectory, moderate climate action',
    ssp: 'SSP2-4.5',
    warming: '2.5-3.0°C',
    color: '#F59E0B',
    factors: { 0: 1.0, 2: 1.05, 4: 1.12, 6: 1.20, 8: 1.30, 10: 1.40 },
  },
  {
    id: 'ssp370',
    name: 'Regional Rivalry (SSP3-7.0)',
    shortName: 'SSP3-7.0',
    description: 'Regional competition, slow technology development',
    ssp: 'SSP3-7.0',
    warming: '3.5-4.5°C',
    color: '#F97316',
    factors: { 0: 1.0, 2: 1.07, 4: 1.15, 6: 1.26, 8: 1.40, 10: 1.55 },
  },
  {
    id: 'ssp585',
    name: 'High Emissions (SSP5-8.5)',
    shortName: 'SSP5-8.5',
    description: 'Fossil fuel development, limited mitigation',
    ssp: 'SSP5-8.5',
    warming: '4.0-5.0°C',
    color: '#EF4444',
    factors: { 0: 1.0, 2: 1.08, 4: 1.18, 6: 1.30, 8: 1.45, 10: 1.62 },
  },
];

// ============================================================================
// DIVESTMENT STRATEGIES
// ============================================================================
const DIVESTMENT_STRATEGIES: DivestmentStrategy[] = [
  {
    id: 'high_risk',
    name: 'High Risk Locations',
    description: 'Divest from locations with overall risk score > threshold',
    threshold: 7,
    metric: 'risk_score',
  },
  {
    id: 'flood_prone',
    name: 'Flood-Prone Areas',
    description: 'Divest from locations with high flood risk',
    threshold: 7,
    metric: 'flood',
  },
  {
    id: 'wildfire_prone',
    name: 'Wildfire Zones',
    description: 'Divest from locations with high wildfire risk',
    threshold: 7,
    metric: 'wildfire',
  },
  {
    id: 'hurricane_belt',
    name: 'Hurricane Belt',
    description: 'Divest from locations with high hurricane exposure',
    threshold: 7,
    metric: 'hurricane',
  },
  {
    id: 'multi_hazard',
    name: 'Multi-Hazard Exposure',
    description: 'Divest from locations with multiple high-risk hazards',
    threshold: 6,
    metric: 'all_hazards',
  },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================
const normalizeAssetValue = (value: any): number => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  const cleaned = String(value).replace(/[$,]/g, '');
  return parseFloat(cleaned) || 0;
};

const formatCurrency = (value: number): string => {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

const formatPercent = (value: number): string => {
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
};

const getScenarioFactor = (scenario: ClimateScenario, year: number): number => {
  if (year <= 0) return scenario.factors[0];
  if (year >= 10) return scenario.factors[10];
  
  const lowerYear = Math.floor(year / 2) * 2;
  const upperYear = Math.ceil(year / 2) * 2;
  
  if (lowerYear === upperYear) return scenario.factors[lowerYear];
  
  const lowerFactor = scenario.factors[lowerYear];
  const upperFactor = scenario.factors[upperYear];
  const progress = (year - lowerYear) / (upperYear - lowerYear);
  
  return lowerFactor + (upperFactor - lowerFactor) * progress;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function ScenarioComparisonTool({ portfolio }: ScenarioComparisonToolProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedScenarios, setSelectedScenarios] = useState<string[]>(['ssp126', 'ssp245', 'ssp585']);
  const [divestmentEnabled, setDivestmentEnabled] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState<string>('high_risk');
  const [customThreshold, setCustomThreshold] = useState(7);
  const [projectionYears, setProjectionYears] = useState(10);
  const [showDivestmentDialog, setShowDivestmentDialog] = useState(false);
  const [manualDivestments, setManualDivestments] = useState<Set<string>>(new Set());
  
  const items = portfolio?.items || [];
  
  // ============================================================================
  // CALCULATE BASE PORTFOLIO METRICS
  // ============================================================================
  const baseMetrics = useMemo(() => {
    const totalValue = items.reduce((sum, item) => sum + normalizeAssetValue(item.asset_value), 0);
    const totalLocations = items.length;
    const avgRisk = items.length > 0 
      ? items.reduce((sum, item) => sum + parseFloat(String(item.risk_score || 0)), 0) / items.length 
      : 0;
    const totalDamage10 = items.reduce((sum, item) => sum + parseFloat(String(item.total_damage_10 || 0)), 0);
    const highRiskCount = items.filter(item => parseFloat(String(item.risk_score || 0)) > 6).length;
    const highRiskValue = items
      .filter(item => parseFloat(String(item.risk_score || 0)) > 6)
      .reduce((sum, item) => sum + normalizeAssetValue(item.asset_value), 0);
    
    return {
      totalValue,
      totalLocations,
      avgRisk,
      totalDamage10,
      highRiskCount,
      highRiskValue,
      annualizedDamage: totalDamage10 / 10,
    };
  }, [items]);
  
  // ============================================================================
  // DIVESTMENT ANALYSIS
  // ============================================================================
  const divestmentAnalysis = useMemo(() => {
    const strategy = DIVESTMENT_STRATEGIES.find(s => s.id === selectedStrategy);
    if (!strategy) return { 
      divested: [] as PortfolioItem[], 
      retained: items, 
      divestedValue: 0,
      divestedDamage: 0,
      retainedValue: baseMetrics.totalValue,
      retainedDamage: baseMetrics.totalDamage10,
      savings: 0,
      newAvgRisk: baseMetrics.avgRisk,
    };
    
    const threshold = customThreshold;
    
    const divested: PortfolioItem[] = [];
    const retained: PortfolioItem[] = [];
    
    items.forEach(item => {
      const itemId = item.postal_code || `${item.city}-${item.state}`;
      
      // Check manual divestments
      if (manualDivestments.has(itemId)) {
        divested.push(item);
        return;
      }
      
      if (!divestmentEnabled) {
        retained.push(item);
        return;
      }
      
      let shouldDivest = false;
      
      switch (strategy.metric) {
        case 'risk_score':
          shouldDivest = parseFloat(String(item.risk_score || 0)) >= threshold;
          break;
        case 'flood':
          shouldDivest = parseFloat(String(item.flood || 0)) >= threshold;
          break;
        case 'wildfire':
          shouldDivest = parseFloat(String(item.wildfire || 0)) >= threshold;
          break;
        case 'hurricane':
          shouldDivest = parseFloat(String(item.hurricane || 0)) >= threshold;
          break;
        case 'all_hazards':
          const hazards = ['flood', 'wildfire', 'hurricane', 'earthquake', 'tornado', 'heat'];
          const highHazardCount = hazards.filter(h => parseFloat(String(item[h] || 0)) >= threshold).length;
          shouldDivest = highHazardCount >= 2;
          break;
      }
      
      if (shouldDivest) {
        divested.push(item);
      } else {
        retained.push(item);
      }
    });
    
    const divestedValue = divested.reduce((sum, item) => sum + normalizeAssetValue(item.asset_value), 0);
    const divestedDamage = divested.reduce((sum, item) => sum + parseFloat(String(item.total_damage_10 || 0)), 0);
    const retainedValue = retained.reduce((sum, item) => sum + normalizeAssetValue(item.asset_value), 0);
    const retainedDamage = retained.reduce((sum, item) => sum + parseFloat(String(item.total_damage_10 || 0)), 0);
    
    return {
      divested,
      retained,
      divestedValue,
      divestedDamage,
      retainedValue,
      retainedDamage,
      savings: divestedDamage,
      newAvgRisk: retained.length > 0 
        ? retained.reduce((sum, item) => sum + parseFloat(String(item.risk_score || 0)), 0) / retained.length 
        : 0,
    };
  }, [items, selectedStrategy, customThreshold, divestmentEnabled, manualDivestments]);
  
  // ============================================================================
  // SCENARIO PROJECTIONS
  // ============================================================================
  const scenarioProjections = useMemo(() => {
    const activeItems = divestmentEnabled ? divestmentAnalysis.retained : items;
    const baseDamage = activeItems.reduce((sum, item) => sum + parseFloat(String(item.total_damage_10 || 0)), 0) / 10;
    const baseValue = activeItems.reduce((sum, item) => sum + normalizeAssetValue(item.asset_value), 0);
    
    const projections: { year: number; [key: string]: number }[] = [];
    
    for (let year = 0; year <= projectionYears; year++) {
      const yearData: { year: number; [key: string]: number } = { year };
      
      CLIMATE_SCENARIOS.forEach(scenario => {
        if (selectedScenarios.includes(scenario.id)) {
          const factor = getScenarioFactor(scenario, year);
          const projectedDamage = baseDamage * factor;
          const cumulativeDamage = Array.from({ length: year + 1 }, (_, y) => 
            baseDamage * getScenarioFactor(scenario, y)
          ).reduce((a, b) => a + b, 0);
          
          yearData[`${scenario.id}_annual`] = projectedDamage;
          yearData[`${scenario.id}_cumulative`] = cumulativeDamage;
          yearData[`${scenario.id}_value`] = baseValue - cumulativeDamage;
        }
      });
      
      projections.push(yearData);
    }
    
    return projections;
  }, [items, divestmentAnalysis, selectedScenarios, projectionYears, divestmentEnabled]);
  
  // ============================================================================
  // COMPARISON SUMMARY
  // ============================================================================
  const comparisonSummary = useMemo(() => {
    const activeItems = divestmentEnabled ? divestmentAnalysis.retained : items;
    const baseDamage = activeItems.reduce((sum, item) => sum + parseFloat(String(item.total_damage_10 || 0)), 0) / 10;
    const baseValue = activeItems.reduce((sum, item) => sum + normalizeAssetValue(item.asset_value), 0);
    
    return CLIMATE_SCENARIOS
      .filter(s => selectedScenarios.includes(s.id))
      .map(scenario => {
        const year10Factor = scenario.factors[10];
        const totalDamage = Array.from({ length: 11 }, (_, y) => 
          baseDamage * getScenarioFactor(scenario, y)
        ).reduce((a, b) => a + b, 0);
        
        const damageIncrease = ((year10Factor - 1) * 100);
        const valueImpact = (totalDamage / baseValue) * 100;
        const annualDamageY10 = baseDamage * year10Factor;
        
        return {
          ...scenario,
          totalDamage,
          damageIncrease,
          valueImpact,
          annualDamageY10,
          annualDamageY0: baseDamage,
          portfolioValueY10: baseValue - totalDamage,
          percentRetained: ((baseValue - totalDamage) / baseValue) * 100,
        };
      });
  }, [items, divestmentAnalysis, selectedScenarios, divestmentEnabled]);
  
  // ============================================================================
  // HANDLERS
  // ============================================================================
  const toggleScenario = (scenarioId: string) => {
    setSelectedScenarios(prev => {
      if (prev.includes(scenarioId)) {
        return prev.filter(id => id !== scenarioId);
      }
      if (prev.length < 4) {
        return [...prev, scenarioId];
      }
      return prev;
    });
  };
  
  const toggleManualDivestment = (itemId: string) => {
    setManualDivestments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };
  
  const resetDivestments = () => {
    setManualDivestments(new Set());
    setDivestmentEnabled(false);
  };
  
  // ============================================================================
  // RENDER
  // ============================================================================
  if (items.length === 0) {
    return (
      <Alert severity="info">
        No portfolio data available for scenario comparison.
      </Alert>
    );
  }
  
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Climate Scenario Comparison Tool
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Compare portfolio performance across IPCC AR6 climate scenarios with divestment simulation
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={() => {
            const data = {
              scenarios: comparisonSummary,
              projections: scenarioProjections,
              divestment: divestmentEnabled ? divestmentAnalysis : null,
            };
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `scenario-comparison-${Date.now()}.json`;
            a.click();
          }}
        >
          Export Analysis
        </Button>
      </Box>

      {/* Scenario Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Select Scenarios to Compare (max 4)
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {CLIMATE_SCENARIOS.map(scenario => (
            <Chip
              key={scenario.id}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: scenario.color }} />
                  {scenario.shortName}
                  <Typography variant="caption" sx={{ ml: 0.5, opacity: 0.7 }}>
                    ({scenario.warming})
                  </Typography>
                </Box>
              }
              onClick={() => toggleScenario(scenario.id)}
              variant={selectedScenarios.includes(scenario.id) ? 'filled' : 'outlined'}
              sx={{
                bgcolor: selectedScenarios.includes(scenario.id) ? `${scenario.color}20` : 'transparent',
                borderColor: scenario.color,
                '&:hover': { bgcolor: `${scenario.color}30` },
              }}
            />
          ))}
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        {/* Divestment Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <FormControlLabel
            control={
              <Switch 
                checked={divestmentEnabled} 
                onChange={(e) => setDivestmentEnabled(e.target.checked)}
                color="warning"
              />
            }
            label={
              <Typography variant="body2" fontWeight={500}>
                Enable Divestment Simulation
              </Typography>
            }
          />
          
          {divestmentEnabled && (
            <>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Strategy</InputLabel>
                <Select
                  value={selectedStrategy}
                  onChange={(e) => setSelectedStrategy(e.target.value)}
                  label="Strategy"
                >
                  {DIVESTMENT_STRATEGIES.map(strategy => (
                    <MenuItem key={strategy.id} value={strategy.id}>
                      {strategy.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">Threshold:</Typography>
                <Slider
                  value={customThreshold}
                  onChange={(_, v) => setCustomThreshold(v as number)}
                  min={3}
                  max={10}
                  step={0.5}
                  valueLabelDisplay="auto"
                  sx={{ width: 100 }}
                />
                <Typography variant="body2" fontWeight={600}>{customThreshold}</Typography>
              </Box>
              
              <Button
                size="small"
                startIcon={<DivestIcon />}
                onClick={() => setShowDivestmentDialog(true)}
              >
                Manual Selection
              </Button>
              
              <Button
                size="small"
                startIcon={<ResetIcon />}
                onClick={resetDivestments}
              >
                Reset
              </Button>
            </>
          )}
        </Box>
      </Paper>

      {/* Divestment Summary Alert */}
      {divestmentEnabled && divestmentAnalysis.divested.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Box>
              <Typography variant="subtitle2">
                Divestment Simulation Active: {divestmentAnalysis.divested.length} locations marked for divestment
              </Typography>
              <Typography variant="body2">
                Value divested: {formatCurrency(divestmentAnalysis.divestedValue || 0)} | 
                Projected savings: {formatCurrency(divestmentAnalysis.savings || 0)} over 10 years | 
                New avg risk: {(divestmentAnalysis.newAvgRisk || 0).toFixed(1)}
              </Typography>
            </Box>
          </Box>
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Comparison Summary" icon={<CompareIcon />} iconPosition="start" />
          <Tab label="Portfolio Value Projection" icon={<TrendingUpIcon />} iconPosition="start" />
          <Tab label="Damage Projections" icon={<WarningIcon />} iconPosition="start" />
          <Tab label="Divestment Impact" icon={<DivestIcon />} iconPosition="start" disabled={!divestmentEnabled} />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Tab 0: Comparison Summary */}
          {activeTab === 0 && (
            <Box>
              <Grid container spacing={3}>
                {comparisonSummary.map((scenario) => (
                  <Grid item xs={12} md={6} lg={3} key={scenario.id}>
                    <Card sx={{ height: '100%', borderTop: `4px solid ${scenario.color}` }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>{scenario.shortName}</Typography>
                            <Typography variant="caption" color="text.secondary">{scenario.warming}</Typography>
                          </Box>
                          <Chip 
                            label={formatPercent(scenario.damageIncrease)} 
                            size="small" 
                            sx={{ bgcolor: `${scenario.color}20`, color: scenario.color }}
                          />
                        </Box>
                        
                        <Divider sx={{ my: 1.5 }} />
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">10-Year Total Damage</Typography>
                            <Typography variant="h6" fontWeight={700} color="error.main">
                              {formatCurrency(scenario.totalDamage)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Year 10 Annual Damage</Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {formatCurrency(scenario.annualDamageY10)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Portfolio Value Retained</Typography>
                            <Typography variant="body1" fontWeight={600} sx={{ color: scenario.percentRetained > 90 ? '#10B981' : scenario.percentRetained > 80 ? '#F59E0B' : '#EF4444' }}>
                              {scenario.percentRetained.toFixed(1)}%
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Value Impact</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {formatCurrency(scenario.portfolioValueY10)} remaining
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              
              {/* Side-by-Side Table */}
              <Paper sx={{ mt: 3, p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Scenario Comparison Table
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#F9FAFB' }}>
                        <TableCell>Metric</TableCell>
                        {comparisonSummary.map(s => (
                          <TableCell key={s.id} align="right">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color }} />
                              {s.shortName}
                            </Box>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Warming by 2100</TableCell>
                        {comparisonSummary.map(s => (
                          <TableCell key={s.id} align="right">{s.warming}</TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell>Damage Increase (Y10 vs Y0)</TableCell>
                        {comparisonSummary.map(s => (
                          <TableCell key={s.id} align="right" sx={{ color: s.color, fontWeight: 600 }}>
                            {formatPercent(s.damageIncrease)}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell>10-Year Cumulative Damage</TableCell>
                        {comparisonSummary.map(s => (
                          <TableCell key={s.id} align="right" sx={{ fontWeight: 600 }}>
                            {formatCurrency(s.totalDamage)}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell>Portfolio Value After 10 Years</TableCell>
                        {comparisonSummary.map(s => (
                          <TableCell key={s.id} align="right">
                            {formatCurrency(s.portfolioValueY10)}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell>Value Retained</TableCell>
                        {comparisonSummary.map(s => (
                          <TableCell key={s.id} align="right">
                            <Chip 
                              label={`${s.percentRetained.toFixed(1)}%`}
                              size="small"
                              sx={{ 
                                bgcolor: s.percentRetained > 90 ? '#D1FAE5' : s.percentRetained > 80 ? '#FEF3C7' : '#FEE2E2',
                                color: s.percentRetained > 90 ? '#059669' : s.percentRetained > 80 ? '#D97706' : '#DC2626',
                              }}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          )}

          {/* Tab 1: Portfolio Value Projection */}
          {activeTab === 1 && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                Projected portfolio value over {projectionYears} years under different climate scenarios
              </Typography>
              
              <Box sx={{ height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={scenarioProjections}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis 
                      dataKey="year" 
                      label={{ value: 'Years', position: 'bottom', offset: -5 }}
                    />
                    <YAxis 
                      tickFormatter={(v) => formatCurrency(v)}
                      label={{ value: 'Portfolio Value', angle: -90, position: 'insideLeft' }}
                    />
                    <RechartsTooltip 
                      formatter={(value: number, name: string) => [formatCurrency(value), name.replace('_value', '')]}
                      labelFormatter={(label) => `Year ${label}`}
                    />
                    <Legend />
                    <ReferenceLine 
                      y={baseMetrics.totalValue} 
                      stroke="#6B7280" 
                      strokeDasharray="5 5"
                      label="Current Value"
                    />
                    {CLIMATE_SCENARIOS.filter(s => selectedScenarios.includes(s.id)).map(scenario => (
                      <Line
                        key={scenario.id}
                        type="monotone"
                        dataKey={`${scenario.id}_value`}
                        name={scenario.shortName}
                        stroke={scenario.color}
                        strokeWidth={2}
                        dot={{ fill: scenario.color, strokeWidth: 2 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
              
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Starting portfolio value: <strong>{formatCurrency(divestmentEnabled ? (divestmentAnalysis.retainedValue || 0) : baseMetrics.totalValue)}</strong>
                  {divestmentEnabled && (
                    <> (after divestment of {formatCurrency(divestmentAnalysis.divestedValue || 0)})</>
                  )}
                </Typography>
              </Alert>
            </Box>
          )}

          {/* Tab 2: Damage Projections */}
          {activeTab === 2 && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                Annual and cumulative damage projections under different scenarios
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Annual Damage by Year</Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={scenarioProjections}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="year" />
                        <YAxis tickFormatter={(v) => formatCurrency(v)} />
                        <RechartsTooltip formatter={(value: number) => formatCurrency(value)} />
                        <Legend />
                        {CLIMATE_SCENARIOS.filter(s => selectedScenarios.includes(s.id)).map(scenario => (
                          <Bar
                            key={scenario.id}
                            dataKey={`${scenario.id}_annual`}
                            name={scenario.shortName}
                            fill={scenario.color}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Cumulative Damage Over Time</Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={scenarioProjections}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="year" />
                        <YAxis tickFormatter={(v) => formatCurrency(v)} />
                        <RechartsTooltip formatter={(value: number) => formatCurrency(value)} />
                        <Legend />
                        {CLIMATE_SCENARIOS.filter(s => selectedScenarios.includes(s.id)).map(scenario => (
                          <Area
                            key={scenario.id}
                            type="monotone"
                            dataKey={`${scenario.id}_cumulative`}
                            name={scenario.shortName}
                            fill={`${scenario.color}30`}
                            stroke={scenario.color}
                            strokeWidth={2}
                          />
                        ))}
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Tab 3: Divestment Impact */}
          {activeTab === 3 && divestmentEnabled && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ bgcolor: '#FEF2F2', height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="error.main" gutterBottom>
                        Divested Assets
                      </Typography>
                      <Typography variant="h4" fontWeight={700} color="error.main">
                        {divestmentAnalysis.divested.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        locations ({formatCurrency(divestmentAnalysis.divestedValue || 0)})
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="body2">
                        Avoided 10-year damage: <strong>{formatCurrency(divestmentAnalysis.divestedDamage || 0)}</strong>
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ bgcolor: '#F0FDF4', height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="success.main" gutterBottom>
                        Retained Assets
                      </Typography>
                      <Typography variant="h4" fontWeight={700} color="success.main">
                        {divestmentAnalysis.retained.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        locations ({formatCurrency(divestmentAnalysis.retainedValue || 0)})
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="body2">
                        Projected 10-year damage: <strong>{formatCurrency(divestmentAnalysis.retainedDamage || 0)}</strong>
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ bgcolor: '#F0F9FF', height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="info.main" gutterBottom>
                        Risk Improvement
                      </Typography>
                      <Typography variant="h4" fontWeight={700} color="info.main">
                        {((baseMetrics.avgRisk - (divestmentAnalysis.newAvgRisk || 0)) / baseMetrics.avgRisk * 100).toFixed(1)}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        reduction in avg risk score
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="body2">
                        New avg risk: <strong>{(divestmentAnalysis.newAvgRisk || 0).toFixed(2)}</strong> (was {baseMetrics.avgRisk.toFixed(2)})
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              
              {/* Divested Locations Table */}
              <Paper sx={{ mt: 3, p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Divested Locations ({divestmentAnalysis.divested.length})
                </Typography>
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Location</TableCell>
                        <TableCell align="right">Risk Score</TableCell>
                        <TableCell align="right">Asset Value</TableCell>
                        <TableCell align="right">10-Year Damage</TableCell>
                        <TableCell align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {divestmentAnalysis.divested.map((item, i) => {
                        const itemId = item.postal_code || `${item.city}-${item.state}`;
                        return (
                          <TableRow key={i}>
                            <TableCell>
                              {item.city || item.postal_code}, {item.state}
                            </TableCell>
                            <TableCell align="right">
                              <Chip 
                                label={parseFloat(String(item.risk_score || 0)).toFixed(1)}
                                size="small"
                                color="error"
                              />
                            </TableCell>
                            <TableCell align="right">{formatCurrency(normalizeAssetValue(item.asset_value))}</TableCell>
                            <TableCell align="right">{formatCurrency(parseFloat(String(item.total_damage_10 || 0)))}</TableCell>
                            <TableCell align="center">
                              <IconButton 
                                size="small" 
                                onClick={() => toggleManualDivestment(itemId)}
                                color="success"
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Manual Divestment Dialog */}
      <Dialog open={showDivestmentDialog} onClose={() => setShowDivestmentDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Manual Divestment Selection</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click on locations to toggle divestment status. Selected locations will be excluded from projections.
          </Typography>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Divest</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell align="right">Risk</TableCell>
                  <TableCell align="right">Value</TableCell>
                  <TableCell align="right">10Y Damage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, i) => {
                  const itemId = item.postal_code || `${item.city}-${item.state}`;
                  const isDivested = manualDivestments.has(itemId);
                  return (
                    <TableRow 
                      key={i} 
                      hover 
                      onClick={() => toggleManualDivestment(itemId)}
                      sx={{ cursor: 'pointer', bgcolor: isDivested ? '#FEE2E2' : 'inherit' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={isDivested} />
                      </TableCell>
                      <TableCell>{item.city || item.postal_code}, {item.state}</TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={parseFloat(String(item.risk_score || 0)).toFixed(1)}
                          size="small"
                          color={parseFloat(String(item.risk_score || 0)) > 6 ? 'error' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">{formatCurrency(normalizeAssetValue(item.asset_value))}</TableCell>
                      <TableCell align="right">{formatCurrency(parseFloat(String(item.total_damage_10 || 0)))}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualDivestments(new Set())}>Clear All</Button>
          <Button onClick={() => setShowDivestmentDialog(false)} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}